/**
 * Form controls and segmented selectors for demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { GRAY, PRIMARY } from "../../design-system/tokens.js";
import { focusRingStyle, MONO_FONT } from "./styles.js";

export interface DemoFieldProps {
  readonly id?: string;
  readonly label: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}

export function DemoField({ id, label, hint, children }: DemoFieldProps): React.JSX.Element {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <label htmlFor={id} style={{ color: GRAY[600], fontSize: 12, fontWeight: 700 }}>
        {label}
      </label>
      {children}
      {hint !== undefined && <div style={{ color: GRAY[500], fontSize: 11 }}>{hint}</div>}
    </div>
  );
}

export interface DemoSliderProps {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly onChange: (value: number) => void;
  readonly help?: string;
  readonly unit?: string;
  readonly format?: (value: number) => string;
}

export function DemoSlider({
  id,
  label,
  value,
  min,
  max,
  step,
  onChange,
  help,
  unit,
  format,
}: DemoSliderProps): React.JSX.Element {
  const display = format !== undefined ? format(value) : value.toFixed(2);
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <div
        style={{ alignItems: "center", display: "flex", gap: 12, justifyContent: "space-between" }}
      >
        <label
          htmlFor={id}
          title={help}
          style={{ color: GRAY[600], fontSize: 12, fontWeight: 700 }}
        >
          {label}
        </label>
        <div
          style={{
            color: GRAY[700],
            fontFamily: MONO_FONT,
            fontSize: 11,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {display}
          {unit !== undefined && <span style={{ color: GRAY[500], marginLeft: 4 }}>{unit}</span>}
        </div>
      </div>
      <input
        id={id}
        aria-label={label}
        title={help ?? label}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => {
          onChange(Number.parseFloat(event.target.value));
        }}
        style={{ accentColor: PRIMARY[600], width: "100%" }}
      />
      {help !== undefined && <div style={{ color: GRAY[500], fontSize: 11 }}>{help}</div>}
    </div>
  );
}

export interface DemoToggleProps {
  readonly id: string;
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly help?: string;
}

export function DemoToggle({
  id,
  label,
  checked,
  onChange,
  help,
}: DemoToggleProps): React.JSX.Element {
  return (
    <label
      htmlFor={id}
      title={help}
      style={{
        alignItems: "center",
        background: checked ? PRIMARY[100] : GRAY[100],
        border: `1px solid ${checked ? PRIMARY[300] : GRAY[300]}`,
        borderRadius: 8,
        cursor: "pointer",
        display: "flex",
        gap: 12,
        justifyContent: "space-between",
        padding: "0.625rem 0.75rem",
      }}
    >
      <span style={{ minWidth: 0 }}>
        <span style={{ color: GRAY[700], display: "block", fontSize: 12, fontWeight: 700 }}>
          {label}
        </span>
        {help !== undefined && (
          <span style={{ color: GRAY[500], display: "block", fontSize: 11 }}>{help}</span>
        )}
      </span>
      <input
        id={id}
        aria-label={label}
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
        style={{ accentColor: PRIMARY[600], flexShrink: 0, height: 16, width: 16 }}
      />
    </label>
  );
}

export interface DemoSegmentedControlProps<T extends string> {
  readonly label: string;
  readonly value: T;
  readonly options: readonly {
    readonly value: T;
    readonly label: string;
    readonly disabled?: boolean;
  }[];
  readonly onChange: (value: T) => void;
}

export function DemoSegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: DemoSegmentedControlProps<T>): React.JSX.Element {
  const groupId = useId();
  const optionId = (optionValue: T): string => `${groupId}-${optionValue}`;
  const focusOption = (optionValue: T): void => {
    if (typeof document === "undefined") return;
    const run = (): void => document.getElementById(optionId(optionValue))?.focus();
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
    else setTimeout(run, 0);
  };
  const handleKey = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    const index = options.findIndex((option) => option.value === value);
    if (index < 0) return;
    event.preventDefault();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    for (let offset = 1; offset <= options.length; offset += 1) {
      const next = options[(index + direction * offset + options.length) % options.length];
      if (next === undefined) continue;
      if (next.disabled !== true) {
        onChange(next.value);
        focusOption(next.value);
        break;
      }
    }
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={handleKey}
      style={{
        background: GRAY[100],
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 8,
        display: "inline-flex",
        gap: 4,
        padding: 4,
      }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            id={optionId(option.value)}
            role="radio"
            type="button"
            aria-checked={selected}
            aria-disabled={option.disabled ?? undefined}
            tabIndex={selected ? 0 : -1}
            disabled={option.disabled}
            onClick={() => {
              if (option.disabled !== true) onChange(option.value);
            }}
            style={{
              ...focusRingStyle(),
              background: selected ? "white" : "transparent",
              border: `1px solid ${selected ? GRAY[300] : "transparent"}`,
              borderRadius: 7,
              boxShadow: selected ? "0 1px 2px rgba(15, 23, 42, 0.05)" : "none",
              color: selected ? GRAY[900] : GRAY[600],
              cursor: option.disabled === true ? "not-allowed" : "pointer",
              fontSize: 12,
              fontWeight: 700,
              opacity: option.disabled === true ? 0.4 : 1,
              padding: "0.4rem 0.85rem",
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
